import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { HrnbTabs, HrnbTab } from '../controls/HrnbTabs';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbModal from '../controls/HrnbModal';
import BattlePairsList from './BattlePairsList';
import TrackVoteModal from './TrackVoteModal';
import RoundSessionControl from './RoundSessionControl';
import TrackReactions from './TrackReactions';
import RoundProgressBar from './RoundProgressBar';
import AudioPlayer from './AudioPlayer';
import '../../css/components/RoundsTabs.css';
import { ROUND_STATUS_OPTIONS } from '../../pages/BattleDetailPage/constants';
import { PlusIcon, ParticipantsIcon, EditIcon, DeleteIcon, DetailsIcon, UploadIcon } from '../../icons';

/**
 * Компонент вкладок раундов баттла
 * 
 * 3 вкладки:
 * 1. Отборочный — треки участников, правила раунда
 * 2. Жеребьёвка — генерация пар, список пар
 * 3. Таблица — сетка плей-офф, результаты
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.rounds - Массив раундов
 * @param {string} props.battleId - UUID баттла
 * @param {boolean} props.isAdmin - Права администратора/организатора
 * @param {Object} props.roundTracks - Объект { roundId: [tracks] }
 * @param {Object} props.roundRules - Объект { roundId: [rules] }
 * @param {Object} props.roundLimitations - Объект { roundId: [limitations] }
 * @param {Function} props.onToggleDetails - Обработчик раскрытия деталей раунда
 * @param {Object} props.expandedDetails - Объект { roundId: boolean }
 * @param {Object} props.loadingDetails - Объект { roundId: boolean } - состояние загрузки деталей
 * @param {Function} props.onCreateRound - Обработчик создания раунда
 * @param {Function} props.onOpenTrackUploader - Обработчик открытия окна загрузки трека
 * @param {Function} props.onEditRound - Обработчик редактирования раунда
 * @param {Function} props.onDeleteRound - Обработчик удаления раунда
 * @param {Function} props.onUpdateStatus - Обработчик изменения статуса раунда
 * @param {Object} props.roundActionLoading - Объект loading состояний действий
 * @param {Function} props.onManageParticipants - Обработчик управления участниками раунда
 * @param {Object} props.currentUser - Текущий пользователь (для проверки участия в баттле)
 * @param {Array} props.battleParticipants - Участники баттла (для проверки роли)
 */
const RoundsTabs = ({
  rounds = [],
  battleId,
  isAdmin = false,
  roundTracks = {},
  roundRules = {},
  roundLimitations = {},
  onToggleDetails,
  expandedDetails = {},
  loadingDetails = {},
  onCreateRound,
  onOpenTrackUploader,
  onEditRound,
  onDeleteRound,
  onUpdateStatus,
  roundActionLoading = {},
  onManageParticipants,
  onSessionClosed,
  currentUser,
  battleParticipants = []
}) => {
  const [activeTab, setActiveTab] = useState('qualifying');
  // Просмотр текста трека: { title, text, loading } | null
  const [viewingText, setViewingText] = useState(null);
  // Оценивание трека судьёй: { track, roundId } | null
  const [votingTrack, setVotingTrack] = useState(null);

  /**
   * Открыть просмотр текста трека.
   * Текст приходит в WorkTrackResponse.track.text
   * (GET /battles/{id}/rounds/{roundId}/tracks) или в work.text.content
   * для текстовых работ — догрузка не требуется.
   */
  const handleViewTrackText = (work, track) => {
    setViewingText({
      title: track?.title || work.text?.title || 'Текст трека',
      text: track?.text || work.text?.content || '',
    });
  };

  // Отладка: краткая сводка по входящим данным
  useEffect(() => {
    console.log('[RoundsTabs] Props:', {
      currentUser: currentUser ? { id: currentUser.id } : null,
      battleParticipantsCount: battleParticipants.length,
      roundsCount: rounds.length
    });
  }, [currentUser, battleParticipants, rounds]);

  // Собираем все возможные ID профилей текущего пользователя
  // (publicProfile.id, profiles[].id, id — разные эндпоинты возвращают разные структуры)
  const getUserProfileIds = useCallback(() => {
    if (!currentUser) return [];
    const ids = [];
    if (currentUser.publicProfile?.id) ids.push(currentUser.publicProfile.id);
    if (Array.isArray(currentUser.profiles)) {
      currentUser.profiles.forEach(p => { if (p?.id) ids.push(p.id); });
    }
    if (currentUser.id) ids.push(currentUser.id);
    return ids;
  }, [currentUser]);

  // Проверка: является ли текущий пользователь участником конкретного раунда
  const isUserParticipantInRound = useCallback((round) => {
    const userProfileIds = getUserProfileIds();
    if (userProfileIds.length === 0) {
      return false;
    }

    // Совпадение по publicProfileId или userId (в разных ответах API поля различаются)
    const isApprovedParticipant = (p) =>
      p.role === 'PARTICIPANT' &&
      p.applicationStatus === 'APPROVED' &&
      (userProfileIds.includes(p.publicProfileId) || userProfileIds.includes(p.userId));

    // Сначала ищем в participants раунда
    if (round?.participants && round.participants.length > 0) {
      if (round.participants.some(isApprovedParticipant)) {
        return true;
      }
    }

    // Fallback: проверяем по общему списку участников баттла
    // (список участников может отсутствовать в ответе раунда)
    const isBattleParticipant = (battleParticipants || []).some(isApprovedParticipant);

    return isBattleParticipant;
  }, [getUserProfileIds, battleParticipants]);

  // Получить ID текущего пользователя в баттле (battlePerson.id = authorId)
  const getCurrentUserPersonId = useCallback((round) => {
    const userProfileIds = getUserProfileIds();
    if (userProfileIds.length === 0) {
      return null;
    }

    const isApprovedParticipant = (p) =>
      p.role === 'PARTICIPANT' &&
      p.applicationStatus === 'APPROVED' &&
      (userProfileIds.includes(p.publicProfileId) || userProfileIds.includes(p.userId));

    // Сначала ищем в participants раунда (более надёжно)
    if (round?.participants && round.participants.length > 0) {
      const roundParticipant = round.participants.find(isApprovedParticipant);
      if (roundParticipant) {
        return roundParticipant.id;
      }
    }

    // Если не нашли в раунде, ищем в общем списке участников баттла
    const participant = (battleParticipants || []).find(isApprovedParticipant);
    return participant?.id || null;
  }, [getUserProfileIds, battleParticipants]);

  // Эффективный список участников раунда: participants раунда,
  // а если API их не вернул — одобренные участники баттла
  const getEffectiveRoundParticipants = useCallback((round) => {
    if (round?.participants && round.participants.length > 0) {
      return round.participants;
    }
    return (battleParticipants || []).filter(
      (p) => p.role === 'PARTICIPANT' && p.applicationStatus === 'APPROVED'
    );
  }, [battleParticipants]);

  // Проверка: является ли текущий пользователь жюри (раунда или баттла)
  const isUserJudge = useCallback((round) => {
    const userProfileIds = getUserProfileIds();
    if (userProfileIds.length === 0) return false;
    const isApprovedJudge = (p) =>
      userProfileIds.includes(p.publicProfileId) && p.role === 'JUDGE' && p.applicationStatus === 'APPROVED';
    if (round?.participants?.length && round.participants.some(isApprovedJudge)) return true;
    return (battleParticipants || []).some(isApprovedJudge);
  }, [getUserProfileIds, battleParticipants]);

  // Группируем раунды по типам
  const roundsByType = useMemo(() => {
    const grouped = {
      QUALIFYING: [],
      DRAW: [],
      PAIR: []
    };

    rounds.forEach(round => {
      if (grouped[round.type]) {
        grouped[round.type].push(round);
      }
    });

    // Сортируем раунды по номеру
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => a.number - b.number);
    });

    return grouped;
  }, [rounds]);

  // Используем expandedDetails как expandedRoundDetails для обратной совместимости
  const expandedRoundDetails = expandedDetails;

  // Определяем, какие вкладки показывать
  const tabs = [
    {
      id: 'qualifying',
      label: 'Отборочный',
      icon: '🎵',
      hasContent: roundsByType.QUALIFYING.length > 0
    },
    {
      id: 'draw',
      label: 'Жеребьёвка',
      icon: '🎲',
      hasContent: roundsByType.DRAW.length > 0
    },
    {
      id: 'table',
      label: 'Таблица',
      icon: '🏆',
      hasContent: roundsByType.PAIR.length > 0
    }
  ];

  // Рендер контента вкладки "Отборочный"
  const renderQualifyingTab = () => {
    if (roundsByType.QUALIFYING.length === 0) {
      return (
        <HrnbAlert variant="info">
          Отборочный раунд ещё не создан
        </HrnbAlert>
      );
    }

    return (
      <div className="rounds-tab-content">
        {roundsByType.QUALIFYING.map(round => {
          const isUserJudgeInRound = isUserJudge(round);
          // Будущие раунды скрываем за баннером (кроме организатора)
          if (isRoundUpcoming(round) && !isAdmin) {
            return <UpcomingRoundBanner key={round.id} round={round} label="Отборочный раунд" />;
          }
          return (
          <HrnbCard
            key={round.id}
            title={`Отборочный раунд ${round.number}: ${round.title}`}
            className="round-card"
          >
            <div className="round-header">
              <span
                className="round-status-badge"
                style={{ backgroundColor: getRoundStatusColor(round.status) }}
              >
                {getRoundStatusLabel(round.status)}
              </span>
              <span className="round-time">
                {formatDate(round.startTime)} — {formatDate(round.endTime)}
              </span>
              {isAdmin && (
                <div className="round-actions">
                  <select
                    className="round-status-select"
                    value={round.status}
                    onChange={(e) => onUpdateStatus?.(round.id, e.target.value)}
                    disabled={roundActionLoading[`status-${round.id}`]}
                    title="Изменить статус раунда"
                  >
                    {ROUND_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {onManageParticipants && (
                    <HrnbButton
                      variant="outline"
                      size="sm"
                      onClick={() => onManageParticipants(round)}
                      title="Управление участниками раунда"
                    >
                      <ParticipantsIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                      Участники
                    </HrnbButton>
                  )}
                  <RoundSessionControl
                    battleId={battleId}
                    roundId={round.id}
                    disabled={roundActionLoading[`status-${round.id}`]}
                    onSessionChange={() => onSessionClosed?.(round.id)}
                  />
                  <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => onEditRound?.(round)}
                  >
                    <EditIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Редактировать
                  </HrnbButton>
                  <HrnbButton
                    variant="danger"
                    size="sm"
                    onClick={() => onDeleteRound?.(round.id)}
                    disabled={roundActionLoading[`status-${round.id}`] || roundActionLoading[round.id]}
                  >
                    <DeleteIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Удалить
                  </HrnbButton>
                </div>
              )}
            </div>

            {/* Прогресс раунда: время + загрузка треков */}
            <RoundProgressBar
              round={round}
              battleId={battleId}
              participantsCount={getEffectiveRoundParticipants(round).length}
              showTracks
            />

            {round.description && (
              <div className="round-description">{round.description}</div>
            )}

            {/* Тема раунда */}
            {round.theme && (
              <div className="round-theme">
                <div className="round-theme-label">🎯 Тема:</div>
                <div className="round-theme-text">{round.theme}</div>
              </div>
            )}

            {/* Задание раунда */}
            {round.task && (
              <div className="round-task">
                <div className="round-task-label">📋 Задание:</div>
                <div className="round-task-text">{round.task}</div>
              </div>
            )}

            {/* Треки раунда */}
            <div className="round-section">
              <div className="round-section-header">
                <h6>Треки участников</h6>
                {(() => {
                  const userIsParticipant = isUserParticipantInRound(round);
                  if (onOpenTrackUploader && userIsParticipant) {
                    return (
                      <button
                        type="button"
                        className="upload-track-btn"
                        onClick={() => onOpenTrackUploader({
                          roundId: round.id,
                          authorId: getCurrentUserPersonId(round),
                          battlePairId: null
                        })}
                        title="Загрузить свой трек"
                      >
                        <UploadIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                        Загрузить свой трек
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
              {getEffectiveRoundParticipants(round).length === 0 ? (
                <HrnbAlert variant="warning">
                  В раунде ещё нет участников. Добавьте участников в баттл перед загрузкой треков.
                </HrnbAlert>
              ) : null}
              {roundTracks[round.id] && roundTracks[round.id].length > 0 ? (
                <div className="tracks-list">
                  {roundTracks[round.id].map(work => {
                    // Новая структура: трек находится в work.track
                    const track = work.track;
                    const hasAudio = work.hasAudio !== undefined ? work.hasAudio : !!track;
                    const hasText = work.hasText !== undefined ? work.hasText : !!work.text;
                    const workStatus = work.status;

                    return (
                      <div key={work.id} className="track-item">
                        <div className="track-content">
                          {hasAudio && track ? (
                            <>
                              {!track.mediaUrl && (
                                <div className="track-info">
                                  <span className="track-title">{track.title || 'Без названия'}</span>
                                  <span className="track-author">{track.authorName || work.authorName || 'Неизвестный автор'}</span>
                                </div>
                              )}
                              {track.mediaUrl && (
                                <div className="track-player">
                                  <RoundTrackPlayer track={{ ...track, authorName: track.authorName || work.authorName }} />
                                </div>
                              )}
                            </>
                          ) : hasText && work.text ? (
                            <div className="text-info">
                              <span className="text-title">{work.text.title || 'Текстовая работа'}</span>
                              <span className="text-author">{work.text.authorName || work.authorName || 'Неизвестный автор'}</span>
                            </div>
                          ) : null}
                        </div>
                        <span className={`track-status track-status--${(workStatus || 'SUBMITTED').toLowerCase()}`}>
                          {workStatus || 'SUBMITTED'}
                        </span>
                        {work.averageScore != null && (
                          <span className="track-score" title="Средняя оценка">
                            ⭐ {Number(work.averageScore).toFixed(1)}
                          </span>
                        )}
                        {isUserJudgeInRound && track?.id && (
                          <HrnbButton
                            variant="primary"
                            size="sm"
                            onClick={() => setVotingTrack({ track, roundId: round.id })}
                            title="Выставить оценку треку"
                          >
                            ⭐ Оценить
                          </HrnbButton>
                        )}
                        {(track?.id || work.text) && (
                          <HrnbButton
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTrackText(work, track)}
                            title="Просмотреть текст трека"
                          >
                            📄 Текст
                          </HrnbButton>
                        )}
                        {track?.id && (
                          <TrackReactions
                            battleId={battleId}
                            roundId={round.id}
                            trackId={track.id}
                            compact
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted">Треки ещё не загружены</p>
              )}
            </div>

            {/* Дедлайны */}
            {round.trackUploadDeadline && (
              <div className="round-deadline">
                ⏰ Дедлайн загрузки треков: {formatDate(round.trackUploadDeadline)}
              </div>
            )}

            {/* Правила и ограничения */}
            <div className="round-details-toggle">
              <HrnbButton
                variant="outline"
                size="sm"
                onClick={() => onToggleDetails?.(round.id)}
              >
                <DetailsIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                {expandedRoundDetails?.[round.id] ? 'Скрыть детали' : 'Правила и ограничения'}
              </HrnbButton>
            </div>

            {expandedRoundDetails?.[round.id] && (
              <div className="round-details">
                {loadingDetails?.[round.id] ? (
                  <div className="round-details-loading">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Загрузка...</span>
                    </div>
                    <span className="ms-2">Загрузка правил и ограничений...</span>
                  </div>
                ) : (
                  <>
                    {renderRoundRules(round.id)}
                    {renderRoundLimitations(round.id)}
                  </>
                )}
              </div>
            )}
          </HrnbCard>
          );
        })}
      </div>
    );
  };

  // Рендер контента вкладки "Жеребьёвка"
  const renderDrawTab = () => {
    const drawRounds = roundsByType.DRAW;

    if (drawRounds.length === 0) {
      return (
        <HrnbAlert variant="info">
          Жеребьёвка ещё не проведена
        </HrnbAlert>
      );
    }

    return (
      <div className="rounds-tab-content">
        {/* Раунды жеребьёвки */}
        {drawRounds.map(round => (
          isRoundUpcoming(round) && !isAdmin ? (
            <UpcomingRoundBanner key={round.id} round={round} label="Жеребьёвка" />
          ) : (
          <HrnbCard
            key={round.id}
            title={`Жеребьёвка: ${round.title}`}
            className="round-card"
          >
            <div className="round-header">
              <span
                className="round-status-badge"
                style={{ backgroundColor: getRoundStatusColor(round.status) }}
              >
                {getRoundStatusLabel(round.status)}
              </span>
              <span className="round-time">
                {formatDate(round.startTime)} — {formatDate(round.endTime)}
              </span>
              {isAdmin && (
                <div className="round-actions">
                  <select
                    className="round-status-select"
                    value={round.status}
                    onChange={(e) => onUpdateStatus?.(round.id, e.target.value)}
                    disabled={roundActionLoading[`status-${round.id}`]}
                    title="Изменить статус раунда"
                  >
                    {ROUND_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => onEditRound?.(round)}
                  >
                    <EditIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Редактировать
                  </HrnbButton>
                  <HrnbButton
                    variant="danger"
                    size="sm"
                    onClick={() => onDeleteRound?.(round.id)}
                    disabled={roundActionLoading[`status-${round.id}`] || roundActionLoading[round.id]}
                  >
                    <DeleteIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Удалить
                  </HrnbButton>
                </div>
              )}
            </div>

            {/* Прогресс времени раунда */}
            <RoundProgressBar round={round} />

            {round.description && (
              <div className="round-description">{round.description}</div>
            )}

            {/* Тема раунда */}
            {round.theme && (
              <div className="round-theme">
                <div className="round-theme-label">🎯 Тема:</div>
                <div className="round-theme-text">{round.theme}</div>
              </div>
            )}

            {/* Задание раунда */}
            {round.task && (
              <div className="round-task">
                <div className="round-task-label">📋 Задание:</div>
                <div className="round-task-text">{round.task}</div>
              </div>
            )}

            {/* Дедлайны */}
            {round.trackUploadDeadline && (
              <div className="round-deadline">
                ⏰ Дедлайн загрузки треков: {formatDate(round.trackUploadDeadline)}
              </div>
            )}

            {round.votingEnabled && round.votingEndTime && (
              <div className="round-deadline">
                🗳️ Голосование до: {formatDate(round.votingEndTime)}
              </div>
            )}
          </HrnbCard>
          )
        ))}
      </div>
    );
  };

  // Рендер контента вкладки "Таблица"
  const renderTableTab = () => {
    if (roundsByType.PAIR.length === 0) {
      return (
        <HrnbAlert variant="info">
          Таблица результатов ещё не сформирована
        </HrnbAlert>
      );
    }

    return (
      <div className="rounds-tab-content">
        {roundsByType.PAIR.map(round => (
          isRoundUpcoming(round) && !isAdmin ? (
            <UpcomingRoundBanner key={round.id} round={round} label="Раунд" />
          ) : (
          <HrnbCard
            key={round.id}
            title={`Раунд ${round.number}: ${round.title}`}
            className="round-card"
          >
            <div className="round-header">
              <span
                className="round-status-badge"
                style={{ backgroundColor: getRoundStatusColor(round.status) }}
              >
                {getRoundStatusLabel(round.status)}
              </span>
              <span className="round-time">
                {formatDate(round.startTime)} — {formatDate(round.endTime)}
              </span>
              {isAdmin && (
                <div className="round-actions">
                  <select
                    className="round-status-select"
                    value={round.status}
                    onChange={(e) => onUpdateStatus?.(round.id, e.target.value)}
                    disabled={roundActionLoading[`status-${round.id}`]}
                    title="Изменить статус раунда"
                  >
                    {ROUND_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => onEditRound?.(round)}
                  >
                    <EditIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Редактировать
                  </HrnbButton>
                  <HrnbButton
                    variant="danger"
                    size="sm"
                    onClick={() => onDeleteRound?.(round.id)}
                    disabled={roundActionLoading[`status-${round.id}`] || roundActionLoading[round.id]}
                  >
                    <DeleteIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Удалить
                  </HrnbButton>
                </div>
              )}
            </div>

            {/* Прогресс времени раунда */}
            <RoundProgressBar round={round} />

            {round.description && (
              <div className="round-description">{round.description}</div>
            )}

            {/* Тема раунда */}
            {round.theme && (
              <div className="round-theme">
                <div className="round-theme-label">🎯 Тема:</div>
                <div className="round-theme-text">{round.theme}</div>
              </div>
            )}

            {/* Задание раунда */}
            {round.task && (
              <div className="round-task">
                <div className="round-task-label">📋 Задание:</div>
                <div className="round-task-text">{round.task}</div>
              </div>
            )}

            {/* Сетка плей-офф или таблица результатов */}
            <div className="playoff-bracket">
              <BattlePairsList
                roundId={round.id}
                isAdmin={isAdmin}
              />
            </div>

            {isAdmin && (
              <div className="round-session-row">
                <RoundSessionControl
                  battleId={battleId}
                  roundId={round.id}
                  disabled={roundActionLoading[`status-${round.id}`]}
                  onSessionChange={() => onSessionClosed?.(round.id)}
                />
              </div>
            )}

            {/* Результаты раунда */}
            <div className="round-results">
              <h6>Результаты раунда</h6>
              {renderRoundResults(round)}
            </div>

            {/* Дедлайны */}
            {round.trackUploadDeadline && (
              <div className="round-deadline">
                ⏰ Дедлайн загрузки треков: {formatDate(round.trackUploadDeadline)}
              </div>
            )}

            {round.votingEnabled && round.votingEndTime && (
              <div className="round-deadline">
                🗳️ Голосование до: {formatDate(round.votingEndTime)}
              </div>
            )}
          </HrnbCard>
          )
        ))}
      </div>
    );
  };

  // Вспомогательные функции
  function getRoundStatusColor(status) {
    const colors = {
      PREPARATION: '#ffc107',
      RUNNING: '#28a745',
      ENDED: '#6c757d'
    };
    return colors[status] || '#6c757d';
  }

  // Проверка: раунд ещё не начался (по статусу или времени старта)
  function isRoundUpcoming(round) {
    if (round.status === 'ENDED') return false;
    if (round.status === 'PREPARATION') return true;
    // RUNNING, но по времени ещё не начался
    if (round.startTime && new Date(round.startTime) > new Date()) return true;
    return false;
  }

  function getRoundStatusLabel(status) {
    const labels = {
      PREPARATION: 'Подготовка',
      RUNNING: 'Идёт',
      ENDED: 'Завершён'
    };
    return labels[status] || status;
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'Не указано';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function renderRoundRules(roundId) {
    const rules = roundRules[roundId];
    console.log(`[RoundsTabs] renderRoundRules for round ${roundId}:`, rules);

    // Если данные ещё не загружены (undefined), ничего не показываем (ждём загрузки)
    if (rules === undefined) {
      console.log(`[RoundsTabs] Rules are undefined, returning null`);
      return null;
    }

    // Если данные загружены, но пустые
    if (!rules || rules.length === 0) {
      console.log(`[RoundsTabs] Rules are empty, showing message`);
      return (
        <div className="round-rules-section">
          <p className="text-muted mb-0">Правила раунда не указаны</p>
        </div>
      );
    }

    console.log(`[RoundsTabs] Rendering ${rules.length} rules`);
    return (
      <div className="round-rules-section">
        <h6>Правила раунда</h6>
        <ul className="round-rules-list">
          {rules.map((rule) => (
            <li key={rule.id} className="round-rule-item">
              <div className="round-rule-content">
                <strong>{rule.name}</strong>
                {rule.description && (
                  <span className="round-rule-desc">{rule.description}</span>
                )}
                {rule.ruleType && (
                  <span className="round-rule-type">Тип: {rule.ruleType}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderRoundLimitations(roundId) {
    const limitations = roundLimitations[roundId];
    console.log(`[RoundsTabs] renderRoundLimitations for round ${roundId}:`, limitations);

    // Если данные ещё не загружены (undefined), ничего не показываем (ждём загрузки)
    if (limitations === undefined) {
      console.log(`[RoundsTabs] Limitations are undefined, returning null`);
      return null;
    }

    // Если данные загружены, но пустые
    if (!limitations || limitations.length === 0) {
      console.log(`[RoundsTabs] Limitations are empty, showing message`);
      return (
        <div className="round-limitations-section">
          <p className="text-muted mb-0">Ограничения раунда не указаны</p>
        </div>
      );
    }

    console.log(`[RoundsTabs] Rendering ${limitations.length} limitations`);
    return (
      <div className="round-limitations-section">
        <h6>Ограничения раунда</h6>
        <div className="round-limitations-list">
          {limitations.map((lim) => (
            <div key={lim.id} className="round-limitation-item">
              <div className="round-limitation-header">
                <strong>{lim.name}</strong>
              </div>
              {lim.description && (
                <div className="round-limitation-desc">{lim.description}</div>
              )}
              <div className="round-limitation-params">
                {lim.minTrackDuration !== null && lim.minTrackDuration !== undefined && (
                  <span className="limitation-param">Мин. длительность: {lim.minTrackDuration} сек</span>
                )}
                {lim.maxTrackDuration !== null && lim.maxTrackDuration !== undefined && (
                  <span className="limitation-param">Макс. длительность: {lim.maxTrackDuration} сек</span>
                )}
                {lim.minTextLength !== null && lim.minTextLength !== undefined && (
                  <span className="limitation-param">Мин. текст: {lim.minTextLength} симв.</span>
                )}
                {lim.maxTextLength !== null && lim.maxTextLength !== undefined && (
                  <span className="limitation-param">Макс. текст: {lim.maxTextLength} симв.</span>
                )}
                {lim.maxFileSizeMb !== null && lim.maxFileSizeMb !== undefined && (
                  <span className="limitation-param">Макс. размер: {lim.maxFileSizeMb} МБ</span>
                )}
                {lim.allowedFormats && (
                  <span className="limitation-param">Форматы: {lim.allowedFormats}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderRoundResults(round) {
    // Здесь можно показать победителей раунда
    return (
      <div className="round-results-placeholder">
        <p className="text-muted">Результаты будут доступны после завершения раунда</p>
      </div>
    );
  }

  return (
    <div className="rounds-tabs-container">
      <div className="rounds-tabs-header">
        <HrnbTabs value={activeTab} onChange={setActiveTab}>
          {tabs.map(tab => (
            <HrnbTab
              key={tab.id}
              value={tab.id}
              label={
                <span className="round-tab-label">
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-text">{tab.label}</span>
                </span>
              }
              disabled={!tab.hasContent && !isAdmin}
            />
          ))}
        </HrnbTabs>

        {isAdmin && (
          <div className="rounds-tabs-actions">
            <HrnbButton
              variant="primary"
              size="sm"
              onClick={() => onCreateRound?.(activeTab)}
              className="create-round-btn"
            >
              <PlusIcon style={{ width: 14, height: 14, marginRight: 6 }} />
              Создать раунд
            </HrnbButton>
          </div>
        )}
      </div>

      <div className="rounds-tab-panel">
        {activeTab === 'qualifying' && renderQualifyingTab()}
        {activeTab === 'draw' && renderDrawTab()}
        {activeTab === 'table' && renderTableTab()}
      </div>

      {/* Модальное окно просмотра текста трека */}
      <HrnbModal
        open={!!viewingText}
        onClose={() => setViewingText(null)}
        title={viewingText?.title}
      >
        {viewingText?.text ? (
          <div className="track-text-view" style={{ whiteSpace: 'pre-wrap' }}>
            {viewingText.text}
          </div>
        ) : (
          <p className="text-muted mb-0">Текст не указан</p>
        )}
      </HrnbModal>

      {/* Модальное окно выставления оценки треку (жюри) */}
      <TrackVoteModal
        open={!!votingTrack}
        onClose={() => setVotingTrack(null)}
        battleId={battleId}
        roundId={votingTrack?.roundId}
        track={votingTrack?.track}
      />
    </div>
  );
}

/**
 * Баннер "Ожидается" — скрывает детали ещё не начавшегося раунда.
 * Показывает только номер/название и время старта.
 * @param {Object} props
 * @param {Object} props.round - Объект раунда
 * @param {string} props.label - Префикс (например "Отборочный раунд")
 */
function UpcomingRoundBanner({ round, label }) {
  return (
    <div className="round-upcoming-banner">
      <span className="round-upcoming-banner__icon">🔒</span>
      <div className="round-upcoming-banner__info">
        <span className="round-upcoming-banner__title">
          {label ? `${label} ${round.number}` : `Раунд ${round.number}`}
          {round.title ? `: ${round.title}` : ''}
        </span>
        <span className="round-upcoming-banner__hint">
          Ожидается{round.startTime ? ` — старт ${formatDateStatic(round.startTime)}` : ''}
        </span>
      </div>
      <span className="round-upcoming-banner__badge">Ожидается</span>
    </div>
  );
}

/** Форматирование даты вне компонента (без зависимостей от замыкания) */
function formatDateStatic(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Компонент плеера для трека в раунде
 * Использует локальный AudioPlayer (battles/AudioPlayer) с загрузкой
 * аудио через fetch с авторизацией — без глобального плеера
 * @param {Object} props
 * @param {Object} props.track - Объект трека (WorkTrackResponse + mediaUrl)
 */
function RoundTrackPlayer({ track }) {
  return (
    <AudioPlayer
      src={track.mediaUrl}
      title={track.title || 'Без названия'}
      artist={track.authorName}
      duration={track.duration}
      showDownload={false}
    />
  );
}

export default RoundsTabs;
